import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
  CadStorageFileDetail,
  CadStorageFileDetailSchema,
  DrawingStorageFileDetail,
  DrawingStorageFileDetailSchema,
  PaginatedCadStorageResponse,
  PaginatedCadStorageResponseSchema,
  PaginatedDrawingStorageResponse,
  PaginatedDrawingStorageResponseSchema,
} from '../../models/storage.model';
import { pagedQueryParams } from '../../utils/list-search';

/** Each kind of stored file has its own endpoint and response schemas. */
const STORAGE_KINDS = {
  cad: {
    path: '/quotes-api/storage/cad-files/',
    listSchema: PaginatedCadStorageResponseSchema,
    detailSchema: CadStorageFileDetailSchema,
  },
  drawing: {
    path: '/quotes-api/storage/drawing-files/',
    listSchema: PaginatedDrawingStorageResponseSchema,
    detailSchema: DrawingStorageFileDetailSchema,
  },
};

export type StorageKind = keyof typeof STORAGE_KINDS;

/** What each kind's endpoints return; the schemas above produce these. */
interface StorageResponses {
  cad: { list: PaginatedCadStorageResponse; detail: CadStorageFileDetail };
  drawing: {
    list: PaginatedDrawingStorageResponse;
    detail: DrawingStorageFileDetail;
  };
}

export interface StorageQuery {
  limit: number;
  offset: number;
  search?: string;
  /** CAD files only. */
  forMoulding?: boolean;
  company?: number | 'none' | null;
  /** Drawing files only: keep drawings that can be picked as an existing drawing. */
  reusable?: boolean;
}

interface StorageFileRef {
  id: number;
}

/** CAD files and drawing PDFs in Storage, each served by its own endpoint. */
@Injectable({ providedIn: 'root' })
export class StorageService {
  private http = inject(HttpClient);

  list<K extends StorageKind>(
    kind: K,
    { limit, offset, search, forMoulding, company, reusable }: StorageQuery,
  ): Observable<StorageResponses[K]['list']> {
    let params = pagedQueryParams({ limit, offset, search });
    if (forMoulding !== undefined) {
      params = params.set('for_moulding', forMoulding.toString());
    }
    if (company !== undefined && company !== null) {
      params = params.set('company', company === 'none' ? '' : String(company));
    }
    if (reusable) {
      params = params.set('reusable', 'true');
    }
    return this.http
      .get<unknown>(STORAGE_KINDS[kind].path, { params })
      .pipe(
        map(
          (raw) =>
            STORAGE_KINDS[kind].listSchema.parse(
              raw,
            ) as StorageResponses[K]['list'],
        ),
      );
  }

  get<K extends StorageKind>(
    kind: K,
    file: StorageFileRef,
  ): Observable<StorageResponses[K]['detail']> {
    return this.http
      .get<unknown>(this.detailUrl(kind, file))
      .pipe(
        map(
          (raw) =>
            STORAGE_KINDS[kind].detailSchema.parse(
              raw,
            ) as StorageResponses[K]['detail'],
        ),
      );
  }

  rename<K extends StorageKind>(
    kind: K,
    file: StorageFileRef,
    filename: string,
  ): Observable<StorageResponses[K]['detail']> {
    return this.http
      .patch<unknown>(this.detailUrl(kind, file), { filename })
      .pipe(
        map(
          (raw) =>
            STORAGE_KINDS[kind].detailSchema.parse(
              raw,
            ) as StorageResponses[K]['detail'],
        ),
      );
  }

  delete(kind: StorageKind, file: StorageFileRef): Observable<void> {
    return this.http.delete<void>(this.detailUrl(kind, file));
  }

  private detailUrl(kind: StorageKind, { id }: StorageFileRef): string {
    return `${STORAGE_KINDS[kind].path}${id}/`;
  }
}
